import { useEffect } from 'react';

import { checkNever } from '@infinex-private/ts-utils/assert';
import {
  MAXIMUM_GAS_ACCOUNT_BALANCE,
  MINIMUM_GAS_ACCOUNT_TOPUP,
} from '@infinex/main-service-contract/v2/client/gas';
import {
  ArrowDownIcon,
  ChevronRightIcon,
  FeeAccountIcon,
  SwapVertIcon,
} from '@infinex/ui-icons';
import {
  Button,
  ButtonGroup,
  Content,
  Emblem,
  Footer,
  SurfaceStack,
  SurfaceStackButtonSegment,
  SurfaceStackFieldSegment,
  SurfaceStackPeek,
  SurfaceStackSegment,
  Text,
  useDialogRouteState,
  YStack,
} from '@infinex/ui/components';

import { useGasBalance } from '../../../api/gas/useGasBalance';
import { AmountInput } from '../../amount-input/AmountInput';
import { TokenSourceWallets } from '../../asset-select/TokenSourceWallets';
import { FormattedPrice } from '../../formatted-number';
import { makeTestId, TEST_ID } from '../../makeTestId';
import { SwitchUnitButton, useCurrencyUnit } from '../../unit-switch';
import { useCustodySelection } from '../../wallet-selector/WalletSelectedProvider';
import { useGasDeposit } from '../GasDepositQuoteProvider';

type Validation = {
  isValid: boolean;
  message?: string;
};

const PRESET_USD_AMOUNTS = [MINIMUM_GAS_ACCOUNT_TOPUP, 1, 5];

const messages = {
  insufficientBalance: 'Insufficient balance',
  minimumTopUp: `Minimum top-up amount is $${MINIMUM_GAS_ACCOUNT_TOPUP}`,
  unsupportedAssets: 'This asset is not supported for gas top ups.',
  maximumTopUp: `Maximum Gas Account balance is $${MAXIMUM_GAS_ACCOUNT_BALANCE}`,
  walletIdRequired: 'Please select a wallet',
  walletNotFound: 'Selected wallet not found',
  noVaultsConfigured: 'No vaults configured',
};

const AddFundsStep = ({
  handleAddFunds,
  isQuoting,
}: {
  handleAddFunds: () => void;
  isQuoting: boolean;
}) => {
  const { selected } = useCustodySelection();
  const { assetSelection, gasQuote, updateUnit } = useGasDeposit();
  const { data: gasBalance } = useGasBalance();
  const { selectedAssetDetails } = assetSelection;
  const dialogRouteState = useDialogRouteState();

  // Find the single source matching the selected custody to display its wallet icon
  const selectedSourceId =
    selected?.custody === 'vault' ? 'vault' : `wallet:${selected?.id}`;
  const selectedSource = selectedAssetDetails?.sources.find(
    (s) => s.id === selectedSourceId
  );

  const { unit, tokenValue, usdValue } = useCurrencyUnit();

  useEffect(() => {
    // sync the unit to the GasDepositQuoteProvider
    updateUnit(unit);
  }, [unit, updateUnit]);

  const currentGasAmount = usdValue ?? 0;
  const currentUSDBalance = selectedAssetDetails?.balanceUsd ?? 0;

  const validation = (): Validation => {
    if (!currentGasAmount) return { isValid: false };
    if (currentGasAmount < MINIMUM_GAS_ACCOUNT_TOPUP) {
      return {
        isValid: false,
        message: messages.minimumTopUp,
      };
    }
    if (currentGasAmount > currentUSDBalance) {
      return {
        isValid: false,
        message: messages.insufficientBalance,
      };
    }

    if (gasBalance + currentGasAmount > MAXIMUM_GAS_ACCOUNT_BALANCE) {
      return {
        isValid: false,
        message: messages.maximumTopUp,
      };
    }

    if (gasQuote?.status === 'rejected') {
      if (gasQuote?.rejectionReason === 'insufficient_balance') {
        return {
          isValid: false,
          message: messages.insufficientBalance,
        };
      }
      if (gasQuote?.rejectionReason === 'no_supported_assets') {
        return {
          isValid: false,
          message: messages.unsupportedAssets,
        };
      }
      if (gasQuote?.rejectionReason === 'exceeds_maximum_balance') {
        return {
          isValid: false,
          message: messages.maximumTopUp,
        };
      }
      if (gasQuote?.rejectionReason === 'wallet_id_required') {
        return {
          isValid: false,
          message: messages.walletIdRequired,
        };
      }
      if (gasQuote?.rejectionReason === 'wallet_not_found') {
        return {
          isValid: false,
          message: messages.walletNotFound,
        };
      }
      if (gasQuote?.rejectionReason === 'no_vaults_configured') {
        return {
          isValid: false,
          message: messages.noVaultsConfigured,
        };
      }
      checkNever(gasQuote.rejectionReason);
    }
    return { isValid: true };
  };

  const { isValid, message } = validation();

  const handleManualIncrease = (value: number) => {
    assetSelection.increaseUsdAmount(value);
  };

  const handleAmountChange = (val?: string) => {
    if (!val) {
      assetSelection.setUserInputAmount();
      return;
    }
    const newAmount = parseFloat(val);
    assetSelection.setUserInputAmount(newAmount);
  };

  return (
    <>
      <Content className="flex-col">
        <YStack gap="12px">
          {/* Asset selector + Amount input */}
          <SurfaceStack>
            <SurfaceStackButtonSegment
              onPress={() => dialogRouteState?.push('select-asset')}
            >
              <Emblem
                src={selectedAssetDetails?.imgUrl}
                chain={selectedAssetDetails?.chain}
                asset="token"
                alt={selectedAssetDetails?.symbol ?? 'Unknown symbol'}
                size="large"
              />

              <YStack minWidth="0px" flex>
                <Text slot="label">{selectedAssetDetails?.symbol}</Text>
                <div className="text-secondary flex min-w-0 items-center gap-1 overflow-hidden text-sm font-semibold">
                  {selectedSource ? (
                    <div className="min-w-0 shrink overflow-hidden">
                      <TokenSourceWallets
                        sources={[selectedSource]}
                        isDisabled={false}
                      />
                    </div>
                  ) : null}
                  <span className="shrink-0">
                    {selectedAssetDetails?.decimalBalance
                      ?.decimalPlaces(6)
                      .toString()}
                  </span>
                </div>
              </YStack>

              <ChevronRightIcon className="text-tertiary" />
            </SurfaceStackButtonSegment>

            <SurfaceStackFieldSegment aria-label="Top-up amount (USD)">
              <div className="grid gap-6">
                <div className="flex flex-col items-center">
                  <AmountInput
                    value={
                      (unit === 'usd'
                        ? assetSelection?.userInputAmount
                            ?.toFixed(2)
                            ?.toString()
                        : assetSelection?.userInputAmount?.toString()) ?? ''
                    }
                    onChange={handleAmountChange}
                    decimals={
                      unit === 'usd' ? 2 : (selectedAssetDetails?.decimals ?? 6)
                    }
                    prefix={unit === 'usd' ? '$' : ''}
                    aria-label="Deposit amount"
                    className="text-emphasis w-full text-center text-4xl font-bold"
                  />

                  <SwitchUnitButton
                    className="self-center"
                    data-testid={makeTestId(
                      TEST_ID.page.gas,
                      'switch-unit-button'
                    )}
                  >
                    <div className="flex items-center justify-center gap-x-1">
                      <FormattedPrice
                        value={
                          unit === 'token'
                            ? (usdValue ?? 0)
                            : (tokenValue?.toNumber() ?? 0)
                        }
                        prefixDollarSign={unit === 'token'}
                        className="text-secondary text-center font-semibold transition-colors duration-200"
                      />
                      <SwapVertIcon size="2xsmall" />
                    </div>
                  </SwitchUnitButton>
                </div>

                <ButtonGroup size="tiny" align="center" className="pb-4">
                  {PRESET_USD_AMOUNTS.map((amount) => (
                    <Button
                      size="tiny"
                      key={amount}
                      onPress={() => handleManualIncrease(amount)}
                      className="hidden sm:block"
                      isDisabled={
                        currentGasAmount + amount > currentUSDBalance ||
                        currentGasAmount + gasBalance + amount >
                          MAXIMUM_GAS_ACCOUNT_BALANCE
                      }
                    >
                      +${amount}
                    </Button>
                  ))}

                  <Button
                    size="tiny"
                    onPress={assetSelection.handleMaxAmount}
                    isDisabled={
                      currentUSDBalance <= 0 ||
                      currentGasAmount >= currentUSDBalance ||
                      MAXIMUM_GAS_ACCOUNT_BALANCE - gasBalance <= 0
                    }
                  >
                    Max
                  </Button>
                </ButtonGroup>
              </div>
            </SurfaceStackFieldSegment>

            <SurfaceStackPeek>
              <div className="bg-fillBrand flex size-8 shrink-0 items-center justify-center rounded-full">
                <ArrowDownIcon size="xsmall" className="fill-brandOnFill" />
              </div>
            </SurfaceStackPeek>

            <SurfaceStackSegment className="flex items-center gap-4 py-3">
              <div className="bg-canvas flex size-10 shrink-0 items-center justify-center rounded-full">
                <FeeAccountIcon fill="brand" size="small" />
              </div>

              <YStack minWidth="0px" flex>
                <Text className="text-secondary text-sm font-semibold">To</Text>
                <Text className="text-emphasis text-base font-semibold">
                  Gas Account
                </Text>
              </YStack>
            </SurfaceStackSegment>
          </SurfaceStack>
        </YStack>
      </Content>

      <Footer className="grid flex-col gap-y-2">
        {message && (
          <span className="text-critical text-center text-xs">{message}</span>
        )}

        <Button
          size="large"
          prominence="high"
          onPress={handleAddFunds}
          isDisabled={!isValid || isQuoting}
        >
          <Text>Review</Text>
        </Button>
      </Footer>
    </>
  );
};

export const AddFundsScreen = () => {
  const dialogRouteState = useDialogRouteState();
  const { gasQuote } = useGasDeposit();

  // Check if quote is loading (allow proceed only when succeeded)
  const isQuoting = !gasQuote || gasQuote.status !== 'succeeded';

  // Handler when user clicks "Top up funds" - show confirmation
  const handleTopUpClick = () => {
    dialogRouteState?.push('review');
  };

  return (
    <AddFundsStep handleAddFunds={handleTopUpClick} isQuoting={isQuoting} />
  );
};
